export interface PasswordStrength {
  score: number;
  strength: 'weak' | 'fair' | 'good' | 'strong';
  feedback: string[];
  isValid: boolean;
}

export interface PasswordRequirements {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
}

const MIN_PASSWORD_LENGTH = 12;

export const checkPasswordRequirements = (password: string): PasswordRequirements => {
  return {
    minLength: password.length >= MIN_PASSWORD_LENGTH,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  };
};

export const validatePassword = (password: string): PasswordStrength => {
  const requirements = checkPasswordRequirements(password);
  const feedback: string[] = [];
  let score = 0;

  if (!requirements.minLength) {
    feedback.push(`Password must be at least ${MIN_PASSWORD_LENGTH} characters long`);
  } else {
    score += 1;
  }

  if (!requirements.hasUppercase) {
    feedback.push('Add at least one uppercase letter');
  } else {
    score += 1;
  }

  if (!requirements.hasLowercase) {
    feedback.push('Add at least one lowercase letter');
  } else {
    score += 1;
  }

  if (!requirements.hasNumber) {
    feedback.push('Add at least one number');
  } else {
    score += 1;
  }

  if (!requirements.hasSpecialChar) {
    feedback.push('Add at least one special character (!@#$%^&*...)');
  } else {
    score += 1;
  }

  if (password.length >= 16) {
    score += 1;
  }

  const commonPasswords = ['password', '12345678', 'qwerty', 'admin', 'letmein', 'welcome'];
  if (commonPasswords.some(common => password.toLowerCase().includes(common))) {
    feedback.push('Avoid common words and patterns');
    score = Math.max(0, score - 2);
  }

  const hasSequentialChars = /(?:abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|012|123|234|345|456|567|678|789)/i.test(password);
  if (hasSequentialChars) {
    feedback.push('Avoid sequential characters');
    score = Math.max(0, score - 1);
  }

  let strength: 'weak' | 'fair' | 'good' | 'strong';
  if (score <= 2) {
    strength = 'weak';
  } else if (score <= 3) {
    strength = 'fair';
  } else if (score <= 4) {
    strength = 'good';
  } else {
    strength = 'strong';
  }

  const isValid = Object.values(requirements).every(req => req === true);

  return {
    score,
    strength,
    feedback,
    isValid,
  };
};

export const getPasswordStrengthColor = (strength: string): string => {
  switch (strength) {
    case 'weak':
      return '#ef4444';
    case 'fair':
      return '#f59e0b';
    case 'good':
      return '#3b82f6';
    case 'strong':
      return '#10b981';
    default:
      return '#9ca3af';
  }
};
